import { PublicKey } from '@solana/web3.js';
import bs58 from 'bs58';

// Define the maximum allowed sum for referrals and myself
const MAX_SUM = 10;

/**
 * Validates a 4-bit number (0-15) and checks the sum constraint.
 * @param {number} referrals - The referrals value to validate.
 * @param {number} myself - The myself value to validate.
 */
const validateInputs = (referrals, myself) => {
    if (referrals < 0 || referrals > 15) {
        throw new Error('Referrals must be a 4-bit number (0-15).');
    }
    if (myself < 0 || myself > 15) {
        throw new Error('Myself must be a 4-bit number (0-15).');
    }
    if (referrals + myself > MAX_SUM) {
        throw new Error(
            `The sum of referrals and myself cannot exceed ${MAX_SUM}.`
        );
    }
};

/**
 * Packs two 4-bit numbers into a single byte.
 * @param {number} high - The high nibble (4-bit number).
 * @param {number} low - The low nibble (4-bit number).
 * @returns {number} - The packed byte.
 */
const packIntoByte = (high, low) => (high << 4) | low;

/**
 * Validates the currency symbol.
 * @param {string} symbol - The currency symbol to validate.
 */
const validateSymbol = (symbol) => {
    if (!/^[A-Z]{1,5}$/.test(symbol)) {
        throw new Error('Symbol must be 1-5 uppercase letters.');
    }
};

/**
 * Generates a Base58-encoded string from the given parameters.
 * @param {string} kickBackAddr - The public key address as a string.
 * @param {number} referrals - A 4-bit number (0-15).
 * @param {number} myself - A 4-bit number (0-15).
 * @param {string} symbol - A currency symbol (1-5 uppercase letters).
 * @returns {string} - The Base58-encoded result.
 */
const linkGen = (kickBackAddr, referrals, myself, symbol) => {
    // Validate inputs
    validateInputs(referrals, myself);
    validateSymbol(symbol);

    // Convert the kickBackAddr to a buffer
    const addressBuffer = new PublicKey(kickBackAddr).toBuffer();

    // Pack referrals and myself into a single byte
    const packedByte = packIntoByte(referrals, myself);

    // Convert the symbol to a fixed 5-byte buffer (padded with spaces if necessary)
    const symbolBuffer = Buffer.alloc(5, ' '); // Default to spaces
    symbolBuffer.write(symbol);

    // Create the final buffer and encode it using Base58
    return bs58.encode(
        Buffer.concat([addressBuffer, Buffer.from([packedByte]), symbolBuffer])
    );
};

/**
 * Decodes a Base58-encoded string to extract the original public key, referrals, myself values, and symbol.
 * @param {string} encoded - The Base58-encoded string.
 * @returns {{ kickBackAddr: string, referrals: number, myself: number, symbol: string }} - The decoded values.
 */
const linkDecode = (encoded) => {
    // Decode the Base58 string to a buffer
    const decodedBuffer = bs58.decode(encoded);

    // Extract the public key buffer (first 32 bytes)
    const addressBuffer = decodedBuffer.slice(0, 32);

    // Extract the packed byte (33rd byte)
    const packedByte = decodedBuffer[32];

    // Extract referrals and myself from the packed byte
    const referrals = (packedByte >> 4) & 0b1111; // High 4 bits
    const myself = packedByte & 0b1111;          // Low 4 bits

    // Extract the symbol (last 5 bytes)
    const symbolBuffer = decodedBuffer.slice(33, 38);
    const symbol = symbolBuffer.toString('utf-8').trim();

    // Check if the decoded values satisfy the MAX_SUM constraint
    if (referrals + myself > MAX_SUM) {
        throw new Error(
            `Decoded values violate the constraint: referrals + myself cannot exceed ${MAX_SUM}.`
        );
    }

    // Convert the address buffer back to a public key string
    const kickBackAddr = new PublicKey(addressBuffer).toString();

    return { kickBackAddr, referrals, myself, symbol };
};

// Export the functions
export { linkGen, linkDecode };
